import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import '@oneclick/react-videolesson/dist/style.css';
import '@oneclick/tangerine-viewer/index.css';
import { DEFAULT_LANG } from 'app/config/config';
import SyncProgressContainer from 'core/modules/components/SyncProgress';
import { getCurrentLanguage } from 'core/utils/languages';
import { createBrowserHistory } from 'history';
import moment from 'moment';
import posthog from 'posthog-js';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Switch } from 'react-router-dom';
import { LastLocationProvider } from 'react-router-last-location';
import { ToastProvider } from 'react-toast-notifications';
import { PersistGate } from 'redux-persist/integration/react';
import AIChat from './app/components/chatbot/AIChat';
import InitsContainer from './app/containers/InitsContainer';
import SessionTimeout from './app/containers/SessionTimeout/SessionTimeout';
import SocketsContainer from './app/containers/SocketsContainer';
import GoogleAuthProvider from './app/providers/GoogleAuthProvider';
import TranslationsProvider from './app/providers/TranslationsProvider';
import { Routes } from './app/router/Routes';
import { RoutesEdelvives } from './edelvives/router/Routes';

import { Toast, ToastContainer } from 'ui';
import { isEdelvives } from './app/utils/utils';
import { I18nProvider, LayoutSplashScreen } from './core';
import DialogGlobalPost from './core/modules/components/dialogs/DialogGlobalPost/DialogGlobalPost';
import DialogInfo from './core/modules/components/dialogs/DialogInfo/DialogInfo';
import analyticsService from './core/services/analyticsService';
import { dedupePosthogSetEvents } from './core/utils/posthogEvents';
import { msalConfig } from './edelvives/config/authConfig';

if (isEdelvives()) {
  import('./edelvives/utils/skinAssets/images');
} else {
  import('./app/utils/skinAssets/images');
}

const msalInstance = new PublicClientApplication(msalConfig);
const history = createBrowserHistory();

// eslint-disable-next-line no-undef
posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
  // eslint-disable-next-line no-undef
  api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  defaults: '2026-01-30',
  before_send: dedupePosthogSetEvents,
});

export default function App({ store, persistor, basename }) {
  const lang = getCurrentLanguage() === 'vl' ? 'ca' : getCurrentLanguage() || DEFAULT_LANG;

  moment.locale(lang);
  // Initialize Analytics Service
  useEffect(() => {
    try {
      analyticsService.initialize({
        // eslint-disable-next-line no-undef
        endpoint: process.env.REACT_APP_ANALYTICS_ENDPOINT,
      });
    } catch (error) {
      console.error('Failed to initialize analytics service:', error);
    }
  }, []);

  const appContent = (
    <Provider store={store}>
      {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
      <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
        {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
        <React.Suspense fallback={<LayoutSplashScreen />}>
          {/* Override `basename` (e.g: `homepage` in `package.json`) */}
          <BrowserRouter basename={basename}>
            {/*This library only returns the location that has been active before the recent location change in the current window lifetime.*/}
            <LastLocationProvider>
              {/* Provide Metronic theme overrides. */}
              {/*<ThemeProvider>*/}
              {/* Provide `react-intl` context synchronized with Redux state.  */}
              <I18nProvider>
                {/* Load translations based on tenant */}
                <TranslationsProvider>
                  <GoogleAuthProvider>
                    {/* Provide `react-toast-notifications` which provides context for the Toast descendants  */}
                    <DialogGlobalPost />
                    <DialogInfo />

                    <ToastProvider autoDismissTimeout={7000} placement="bottom-left" components={{ Toast, ToastContainer }}>
                      {/* Render routes with provided `Layout`. */}
                      <AIChat>
                        <Switch>{isEdelvives() ? <RoutesEdelvives history={history} /> : <Routes history={history} />}</Switch>
                      </AIChat>
                      <SocketsContainer />
                      <InitsContainer />
                      <SessionTimeout />
                      <SyncProgressContainer />
                    </ToastProvider>
                  </GoogleAuthProvider>
                </TranslationsProvider>
              </I18nProvider>
              {/*</ThemeProvider>*/}
            </LastLocationProvider>
          </BrowserRouter>
        </React.Suspense>
      </PersistGate>
    </Provider>
  );

  // Solo envolver con MsalProvider si es Edelvives y msalInstance está disponible
  if (isEdelvives() && msalInstance) {
    return <MsalProvider instance={msalInstance}>{appContent}</MsalProvider>;
  }

  return appContent;
}

App.propTypes = {
  basename: PropTypes.string,
  persistor: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
};
